// ============================================================
// Student ID Card Component
// ============================================================
// Professional, printable dual-sided Student Identity Card.
// Uses native, bulletproof styling to ensure perfect rendering 
// on-screen, during PNG download, and in print windows.

import { useState, useRef } from 'react';
import { Box, Typography, Button, Avatar, CircularProgress } from '@mui/material';
import { Printer, Phone, QrCode, Download } from 'lucide-react';
import { FcLibrary } from 'react-icons/fc';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import { tokens } from '@/theme/tokens';

function stringToColor(string) {
  if (!string) return '#1e293b';
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

export function StudentIDCard({ fullData }) {
  const cardRef = useRef(null);
  const [layout, setLayout] = useState('vertical'); // 'vertical' | 'horizontal'
  const [downloading, setDownloading] = useState(false);

  const student = fullData?.student || {};
  const parent = fullData?.parent || {};
  const address = fullData?.address || {};
  const enrollment = fullData?.enrollment || {};

  const school = enrollment.school || {};
  const schoolName = school.school_name || 'Bright Future International School';
  const schoolAddress = [school.address, school.city, school.state].filter(Boolean).join(', ') || 'Lucknow, Uttar Pradesh';
  const schoolPhone = school.phone || '+91 9876543203';

  const rawSession =
    enrollment.academic_session?.session_name ||
    enrollment.academic_session_id?.session_name ||
    enrollment.academic_session_id ||
    '2025-2026';
  const sessionName = rawSession.replace(/^Academic\s*session\s*/i, '');

  const className =
    enrollment.class?.class_name ||
    enrollment.class_id?.class_name ||
    enrollment.class_id ||
    '-';

  const sectionName =
    enrollment.section?.section_name ||
    enrollment.section_id?.section_name ||
    enrollment.section_id ||
    '-';

  const firstName = student.first_name || student.firstName || '';
  const middleName = student.middle_name || '';
  const lastName = student.last_name || student.lastName || '';
  const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ') || 'Student Name';

  const handleDownload = async () => {
    const element = cardRef.current;
    if (!element) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(element, {
        scale: 3, // High DPI crisp rendering
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `${fullName.replace(/\s+/g, '_')}_ID_Card_${layout}.png`;
      link.click();
      toast.success('ID Card downloaded successfully!');
    } catch (err) {
      console.error('ID Card Download error:', err);
      toast.error('Failed to download ID Card');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    const printContent = cardRef.current;
    if (!printContent) return;

    // Collect all stylesheets from current document to keep styles intact
    const styleTags = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');

    const printWindow = window.open('', '', 'width=950,height=750');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Student ID Card - ${fullName}</title>
          ${styleTags}
          <style>
            @page {
              size: A4 ${layout === 'vertical' ? 'landscape' : 'portrait'};
              margin: 10mm;
            }
            *, *::before, *::after {
              box-sizing: border-box;
            }
            body {
              font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
              background-color: #ffffff !important;
              margin: 0 !important;
              padding: 20px !important;
              display: flex !important;
              justify-content: center !important;
              align-items: center !important;
              min-height: 100vh !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          </style>
        </head>
        <body>
          <div style="display: flex; justify-content: center; align-items: center; width: 100%;">
            ${printContent.innerHTML}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, my: 2 }}>
      {/* Top Action Bar with Layout Toggle */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          width: '100%',
          maxWidth: layout === 'horizontal' ? 980 : 720,
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          p: 1.5,
          borderRadius: tokens.radius.md,
          boxShadow: tokens.shadows.sm,
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Layout Switcher */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mr: 0.5 }}>
            Layout:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#f1f5f9', p: 0.5, borderRadius: '8px' }}>
            <Button
              size="small"
              variant={layout === 'vertical' ? 'contained' : 'text'}
              color="primary"
              onClick={() => setLayout('vertical')}
              sx={{ borderRadius: '6px', textTransform: 'none', fontWeight: 600, py: 0.5 }}
            >
              Vertical
            </Button>
            <Button
              size="small"
              variant={layout === 'horizontal' ? 'contained' : 'text'}
              color="primary"
              onClick={() => setLayout('horizontal')}
              sx={{ borderRadius: '6px', textTransform: 'none', fontWeight: 600, py: 0.5 }}
            >
              Horizontal
            </Button>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <Download size={18} />}
            onClick={handleDownload}
            disabled={downloading}
            sx={{ borderRadius: tokens.radius.md, px: 2.5, fontWeight: 600 }}
          >
            {downloading ? 'Downloading...' : 'Download ID Card'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Printer size={18} />}
            onClick={handlePrint}
            sx={{ borderRadius: tokens.radius.md, px: 2.5, fontWeight: 600 }}
          >
            Print ID Card
          </Button>
        </Box>
      </Box>

      {/* Cards Container */}
      <div
        ref={cardRef}
        style={{
          display: 'flex',
          flexDirection: layout === 'horizontal' ? 'column' : 'row',
          gap: '32px',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          backgroundColor: '#ffffff',
        }}
      >
        {/* ============================================================ */}
        {/* VERTICAL LAYOUT (PORTRAIT) */}
        {/* ============================================================ */}
        {layout === 'vertical' && (
          <>
            {/* FRONT SIDE (VERTICAL) */}
            <div
              style={{
                width: '330px',
                minHeight: '510px',
                borderRadius: '16px',
                backgroundColor: '#ffffff',
                border: '1.5px solid #e2e8f0',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                boxSizing: 'border-box',
              }}
            >
              {/* Header Banner - Solid Navy */}
              <div
                style={{
                  backgroundColor: '#0f172a',
                  color: '#ffffff',
                  paddingTop: '18px',
                  paddingBottom: '32px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <FcLibrary size={26} />
                  <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '15px', lineHeight: 1.2 }}>
                    {schoolName}
                  </div>
                </div>
                <div style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 500 }}>
                  {schoolAddress}
                </div>
                <div
                  style={{
                    marginTop: '8px',
                    padding: '3px 14px',
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    borderRadius: '20px',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.8px',
                    textTransform: 'uppercase',
                  }}
                >
                  STUDENT IDENTITY CARD
                </div>
              </div>

              {/* Accent Line */}
              <div style={{ height: '4px', backgroundColor: '#0284c7' }} />

              {/* Student Photo & Name Section */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '12px', paddingBottom: '8px', paddingLeft: '16px', paddingRight: '16px' }}>
                <div style={{ marginTop: '-44px', marginBottom: '8px' }}>
                  <Avatar
                    src={student.photo}
                    sx={{
                      width: 84,
                      height: 84,
                      border: '4px solid #ffffff',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                      bgcolor: stringToColor(student.admission_no || student.id || ''),
                      color: '#ffffff',
                      fontSize: '2.2rem',
                      fontWeight: 700,
                    }}
                  >
                    {(firstName[0] || 'S').toUpperCase()}
                  </Avatar>
                </div>
                <div style={{ fontWeight: 700, textAlign: 'center', color: '#0f172a', fontSize: '16px', lineHeight: 1.2 }}>
                  {fullName}
                </div>
                <div
                  style={{
                    marginTop: '8px',
                    padding: '4px 14px',
                    borderRadius: '16px',
                    fontWeight: 700,
                    fontSize: '12px',
                    lineHeight: 1.2,
                    backgroundColor: '#eff6ff',
                    color: '#1d4ed8',
                    border: '1px solid #bfdbfe',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {`Class: ${className} - ${sectionName}`}
                </div>
              </div>

              {/* Key Details Container - Bulletproof Native Stack */}
              <div style={{ margin: '8px 16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', rowGap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>
                      Father's Name
                    </div>
                    <div style={{ color: '#0f172a', fontSize: '12px', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {parent.father_name || parent.guardian_name || '-'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>
                      Contact No
                    </div>
                    <div style={{ color: '#0f172a', fontSize: '12px', fontWeight: 700, lineHeight: 1.2 }}>
                      {parent.father_mobile || parent.guardian_mobile || student.mobile || '-'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>
                      Admission No
                    </div>
                    <div style={{ color: '#0f172a', fontSize: '12px', fontWeight: 700, lineHeight: 1.2 }}>
                      {student.admission_no || '-'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>
                      Roll Number
                    </div>
                    <div style={{ color: '#0f172a', fontSize: '12px', fontWeight: 700, lineHeight: 1.2 }}>
                      {enrollment.roll_number || '-'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>
                      Blood Group
                    </div>
                    <div style={{ color: '#dc2626', fontSize: '12px', fontWeight: 700, lineHeight: 1.2 }}>
                      {student.blood_group || '-'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px' }}>
                      Date of Birth
                    </div>
                    <div style={{ color: '#0f172a', fontSize: '12px', fontWeight: 700, lineHeight: 1.2 }}>
                      {student.date_of_birth || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Bar */}
              <div
                style={{
                  marginTop: 'auto',
                  backgroundColor: '#ffffff',
                  borderTop: '1px solid #e2e8f0',
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ padding: '4px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <QrCode size={24} color="#334155" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 600, lineHeight: 1 }}>
                      SESSION
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#0f172a', marginTop: '2px', lineHeight: 1 }}>
                      {sessionName}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '9px', color: '#64748b', fontStyle: 'italic', lineHeight: 1 }}>
                    Auth. Signature
                  </div>
                  <div style={{ fontFamily: 'cursive, sans-serif', fontWeight: 700, color: '#1e293b', fontSize: '13px', marginTop: '2px' }}>
                    Principal
                  </div>
                </div>
              </div>
            </div>

            {/* BACK SIDE (VERTICAL) */}
            <div
              style={{
                width: '330px',
                minHeight: '510px',
                borderRadius: '16px',
                backgroundColor: '#ffffff',
                border: '1.5px solid #e2e8f0',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ backgroundColor: '#0f172a', color: '#ffffff', padding: '16px', textAlign: 'center' }}>
                <div style={{ fontWeight: 700, letterSpacing: '0.8px', color: '#ffffff', fontSize: '13px' }}>
                  EMERGENCY & ADDRESS DETAILS
                </div>
              </div>

              <div style={{ height: '4px', backgroundColor: '#0284c7' }} />

              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
                <div>
                  <div style={{ color: '#2563eb', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '10px' }}>
                    Parent / Guardian Information
                  </div>
                  <div style={{ color: '#0f172a', fontWeight: 600, fontSize: '13px' }}>
                    Father: {parent.father_name || '-'}
                  </div>
                  <div style={{ color: '#475569', fontSize: '12px', marginTop: '2px' }}>
                    Contact: {parent.father_mobile || parent.guardian_mobile || student.mobile || '-'}
                  </div>
                </div>

                <div style={{ borderBottom: '1px solid #f1f5f9' }} />

                <div>
                  <div style={{ color: '#2563eb', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '10px' }}>
                    Residential Address
                  </div>
                  <div style={{ color: '#0f172a', fontSize: '12px', lineHeight: 1.4, fontWeight: 500 }}>
                    {[address.current_address, address.current_city, address.current_state, address.current_pincode].filter(Boolean).join(', ') || 'N/A'}
                  </div>
                </div>

                <div style={{ borderBottom: '1px solid #f1f5f9' }} />

                <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <Phone size={14} color="#1d4ed8" />
                    <div style={{ color: '#1d4ed8', fontWeight: 700, fontSize: '11px' }}>
                      Emergency School Helpline
                    </div>
                  </div>
                  <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '13px' }}>
                    {schoolPhone}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 'auto', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', color: '#64748b', lineHeight: 1.3, fontWeight: 500 }}>
                  If found, please return to: {schoolName}, {schoolAddress}.
                </div>
              </div>
            </div>
          </>
        )}

        {/* ============================================================ */}
        {/* HORIZONTAL LAYOUT (LANDSCAPE) */}
        {/* ============================================================ */}
        {layout === 'horizontal' && (
          <>
            {/* FRONT SIDE (HORIZONTAL) */}
            <div
              style={{
                width: '480px',
                minHeight: '290px',
                borderRadius: '16px',
                backgroundColor: '#ffffff',
                border: '1.5px solid #e2e8f0',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                boxSizing: 'border-box',
              }}
            >
              {/* Top Banner */}
              <div
                style={{
                  backgroundColor: '#0f172a',
                  color: '#ffffff',
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FcLibrary size={24} />
                  <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '14px' }}>
                    {schoolName}
                  </div>
                </div>
                <div
                  style={{
                    padding: '2px 10px',
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    borderRadius: '12px',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  STUDENT ID CARD
                </div>
              </div>

              <div style={{ height: '3px', backgroundColor: '#0284c7' }} />

              {/* Main Content Body */}
              <div style={{ display: 'flex', padding: '14px', gap: '16px', flex: 1 }}>
                {/* Left Photo & Class Column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '110px' }}>
                  <Avatar
                    src={student.photo}
                    sx={{
                      width: 76,
                      height: 76,
                      border: '3px solid #ffffff',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
                      bgcolor: stringToColor(student.admission_no || student.id || ''),
                      color: '#ffffff',
                      fontSize: '2rem',
                      fontWeight: 700,
                    }}
                  >
                    {(firstName[0] || 'S').toUpperCase()}
                  </Avatar>
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontWeight: 700,
                      fontSize: '11px',
                      lineHeight: 1.2,
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                      border: '1px solid #bfdbfe',
                      textAlign: 'center',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {`Class: ${className}-${sectionName}`}
                  </div>
                </div>

                {/* Right Details Column */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2, fontSize: '15px', marginBottom: '8px' }}>
                    {fullName}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 600, uppercase: 'true', marginBottom: '2px' }}>
                        Father's Name
                      </div>
                      <div style={{ color: '#0f172a', fontSize: '11px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {parent.father_name || parent.guardian_name || '-'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 600, uppercase: 'true', marginBottom: '2px' }}>
                        Contact No
                      </div>
                      <div style={{ color: '#0f172a', fontSize: '11px', fontWeight: 700 }}>
                        {parent.father_mobile || parent.guardian_mobile || student.mobile || '-'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 600, uppercase: 'true', marginBottom: '2px' }}>
                        Admission No
                      </div>
                      <div style={{ color: '#0f172a', fontSize: '11px', fontWeight: 700 }}>
                        {student.admission_no || '-'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 600, uppercase: 'true', marginBottom: '2px' }}>
                        Roll Number
                      </div>
                      <div style={{ color: '#0f172a', fontSize: '11px', fontWeight: 700 }}>
                        {enrollment.roll_number || '-'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 600, uppercase: 'true', marginBottom: '2px' }}>
                        Blood Group
                      </div>
                      <div style={{ color: '#dc2626', fontSize: '11px', fontWeight: 700 }}>
                        {student.blood_group || '-'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{ color: '#64748b', fontSize: '9px', fontWeight: 600, uppercase: 'true', marginBottom: '2px' }}>
                        Date of Birth
                      </div>
                      <div style={{ color: '#0f172a', fontSize: '11px', fontWeight: 700 }}>
                        {student.date_of_birth || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Footer Bar */}
              <div
                style={{
                  marginTop: 'auto',
                  backgroundColor: '#ffffff',
                  borderTop: '1px solid #e2e8f0',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <QrCode size={20} color="#334155" />
                  <div style={{ fontWeight: 700, fontSize: '11px', color: '#0f172a' }}>
                    SESSION: {sessionName}
                  </div>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ fontSize: '9px', color: '#64748b', fontStyle: 'italic' }}>
                    Auth. Signature:
                  </div>
                  <div style={{ fontFamily: 'cursive, sans-serif', fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>
                    Principal
                  </div>
                </div>
              </div>
            </div>

            {/* BACK SIDE (HORIZONTAL) */}
            <div
              style={{
                width: '480px',
                minHeight: '290px',
                borderRadius: '16px',
                backgroundColor: '#ffffff',
                border: '1.5px solid #e2e8f0',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  backgroundColor: '#0f172a',
                  color: '#ffffff',
                  padding: '10px 16px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontWeight: 700, letterSpacing: '0.8px', color: '#ffffff', fontSize: '13px' }}>
                  EMERGENCY & ADDRESS DETAILS
                </div>
              </div>

              <div style={{ height: '3px', backgroundColor: '#0284c7' }} />

              <div style={{ display: 'flex', padding: '14px', gap: '16px', flex: 1 }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <div style={{ color: '#2563eb', fontWeight: 700, marginBottom: '3px', letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '10px' }}>
                      Parent / Guardian Info
                    </div>
                    <div style={{ color: '#0f172a', fontWeight: 600, fontSize: '12px' }}>
                      Father: {parent.father_name || '-'}
                    </div>
                    <div style={{ color: '#475569', fontSize: '11px', marginTop: '2px' }}>
                      Mobile: {parent.father_mobile || parent.guardian_mobile || student.mobile || '-'}
                    </div>
                  </div>

                  <div style={{ padding: '8px 10px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                      <Phone size={12} color="#1d4ed8" />
                      <div style={{ color: '#1d4ed8', fontWeight: 700, fontSize: '10px' }}>
                        Emergency Helpline
                      </div>
                    </div>
                    <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '12px' }}>
                      {schoolPhone}
                    </div>
                  </div>
                </div>

                <div style={{ borderRight: '1px solid #f1f5f9' }} />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <div style={{ color: '#2563eb', fontWeight: 700, marginBottom: '3px', letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '10px' }}>
                      Residential Address
                    </div>
                    <div style={{ color: '#0f172a', fontSize: '11px', lineHeight: 1.35, fontWeight: 500 }}>
                      {[address.current_address, address.current_city, address.current_state, address.current_pincode].filter(Boolean).join(', ') || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 'auto',
                  backgroundColor: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  padding: '8px 12px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '9.5px', color: '#64748b', lineHeight: 1.2, fontWeight: 500 }}>
                  If found, please return to: {schoolName}, {schoolAddress}.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Box>
  );
}
